import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useU2000AxiosAuthenticated from '../../u2000Core/axios/U2000UseAxiosAuthenticated';
import { U2000HandleApiError, type U2000ApiErrorResult } from '../../u2000Core/axios/U2000HandleApiError';
import { useToast } from '../../u2000Core/context/toast/U2000ToastContext';
import { NS } from '../U3207';
import {
  doGetConversationsDrilldown,
  doGetDashboardSummary,
  doGetGroupDrilldown,
  doGetProviderDrilldown,
  doGetUserDrilldown,
  luHandleAppiError,
  type U3207ConversationsDrilldown,
  type U3207DashboardQuery,
  type U3207DashboardSummary,
  type U3207GroupDrilldown,
  type U3207ProviderDrilldown,
  type U3207UserDrilldown,
} from '../U3207Service';

export function useU3207DashboardHandlers() {
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation(NS);
  const toast = useToast();

  const [summary, setSummary] = useState<U3207DashboardSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState(false);

  const [drillLoading, setDrillLoading] = useState(false);
  const [providerDrill, setProviderDrill] = useState<U3207ProviderDrilldown | null>(null);
  const [groupDrill, setGroupDrill] = useState<U3207GroupDrilldown | null>(null);
  const [userDrill, setUserDrill] = useState<U3207UserDrilldown | null>(null);
  const [conversationsDrill, setConversationsDrill] = useState<U3207ConversationsDrilldown | null>(
    null,
  );

  const handleError = useCallback(
    (error: unknown) => {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError) as U2000ApiErrorResult;
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
    [t, toast],
  );

  const loadSummary = useCallback(
    async (query: U3207DashboardQuery) => {
      setSummaryLoading(true);
      setSummaryError(false);
      try {
        const response = await doGetDashboardSummary(axios, query);
        const next = (response.data?.result as U3207DashboardSummary) ?? null;
        setSummary(next);
        return next;
      } catch (error: unknown) {
        handleError(error);
        setSummary(null);
        setSummaryError(true);
        return null;
      } finally {
        setSummaryLoading(false);
      }
    },
    [axios, handleError],
  );

  const loadProviderDrill = useCallback(
    async (providerId: string, query: U3207DashboardQuery) => {
      setDrillLoading(true);
      try {
        const response = await doGetProviderDrilldown(axios, providerId, query);
        const next = (response.data?.result as U3207ProviderDrilldown) ?? null;
        setProviderDrill(next);
        setGroupDrill(null);
        setUserDrill(null);
        setConversationsDrill(null);
        return next;
      } catch (error: unknown) {
        handleError(error);
        setProviderDrill(null);
        return null;
      } finally {
        setDrillLoading(false);
      }
    },
    [axios, handleError],
  );

  const loadGroupDrill = useCallback(
    async (groupId: string, query: U3207DashboardQuery) => {
      setDrillLoading(true);
      try {
        const response = await doGetGroupDrilldown(axios, groupId, query);
        const next = (response.data?.result as U3207GroupDrilldown) ?? null;
        setGroupDrill(next);
        setProviderDrill(null);
        setUserDrill(null);
        setConversationsDrill(null);
        return next;
      } catch (error: unknown) {
        handleError(error);
        setGroupDrill(null);
        return null;
      } finally {
        setDrillLoading(false);
      }
    },
    [axios, handleError],
  );

  const loadUserDrill = useCallback(
    async (userId: string, query: U3207DashboardQuery) => {
      setDrillLoading(true);
      try {
        const response = await doGetUserDrilldown(axios, userId, query);
        const next = (response.data?.result as U3207UserDrilldown) ?? null;
        setUserDrill(next);
        setProviderDrill(null);
        setGroupDrill(null);
        setConversationsDrill(null);
        return next;
      } catch (error: unknown) {
        handleError(error);
        setUserDrill(null);
        return null;
      } finally {
        setDrillLoading(false);
      }
    },
    [axios, handleError],
  );

  const loadConversationsDrill = useCallback(
    async (query: U3207DashboardQuery) => {
      setDrillLoading(true);
      try {
        const response = await doGetConversationsDrilldown(axios, query);
        const next = (response.data?.result as U3207ConversationsDrilldown) ?? null;
        setConversationsDrill(next);
        setProviderDrill(null);
        setGroupDrill(null);
        setUserDrill(null);
        return next;
      } catch (error: unknown) {
        handleError(error);
        setConversationsDrill(null);
        return null;
      } finally {
        setDrillLoading(false);
      }
    },
    [axios, handleError],
  );

  const clearDrill = useCallback(() => {
    setProviderDrill(null);
    setGroupDrill(null);
    setUserDrill(null);
    setConversationsDrill(null);
  }, []);

  return {
    summary,
    summaryLoading,
    summaryError,
    loadSummary,
    drillLoading,
    providerDrill,
    groupDrill,
    userDrill,
    conversationsDrill,
    loadProviderDrill,
    loadGroupDrill,
    loadUserDrill,
    loadConversationsDrill,
    clearDrill,
  };
}
